import {
  type AddDocumentVersionInput,
  type CreateDocumentInput,
  type DocumentDetail,
  type DocumentKind,
  type DocumentListView,
  type DocumentStatus,
  type DocumentTemplateView,
  type DocumentVersionContent,
  type ReorderDocumentsInput,
  type ReviewDocumentInput,
  type SaveDocumentInput,
  type SaveDocumentResult,
  type UpdateDocumentInput,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

export interface DocumentQuery {
  readonly q?: string;
  readonly kind?: DocumentKind | '';
  readonly status?: DocumentStatus | '';
}

function toSearch(query: DocumentQuery): string {
  const params = new URLSearchParams();
  if (query.q?.trim()) params.set('q', query.q.trim());
  if (query.kind) params.set('kind', query.kind);
  if (query.status) params.set('status', query.status);

  const search = params.toString();
  return search ? `?${search}` : '';
}

export const documentApi = {
  list: (projectId: string, query: DocumentQuery) =>
    apiFetch<DocumentListView>(`/projects/${projectId}/documents${toSearch(query)}`),

  detail: (documentId: string) => apiFetch<DocumentDetail>(`/documents/${documentId}`),

  version: (documentId: string, versionNo: number) =>
    apiFetch<DocumentVersionContent>(`/documents/${documentId}/versions/${versionNo}`),

  create: (projectId: string, input: CreateDocumentInput) =>
    apiFetch<DocumentDetail>(`/projects/${projectId}/documents`, { method: 'POST', body: input }),

  update: (documentId: string, input: UpdateDocumentInput) =>
    apiFetch<DocumentDetail>(`/documents/${documentId}`, { method: 'PATCH', body: input }),

  addVersion: (documentId: string, input: AddDocumentVersionInput) =>
    apiFetch<DocumentDetail>(`/documents/${documentId}/versions`, { method: 'POST', body: input }),

  /** "Lưu" của hộp soạn: ghi đè bản mới nhất khi được phép, không thì thêm bản (APM-PDOC-f). */
  save: (documentId: string, input: SaveDocumentInput) =>
    apiFetch<SaveDocumentResult>(`/documents/${documentId}/save`, { method: 'POST', body: input }),

  /** `submit` | `approve` | `reject` — ba bước của workflow (spec mục 7). */
  transition: (documentId: string, action: string, input: ReviewDocumentInput = {}) =>
    apiFetch<DocumentDetail>(`/documents/${documentId}/${action}`, { method: 'POST', body: input }),

  /** Thứ tự sắp tay của kho tài liệu — cả danh sách theo thứ tự mới (APM-DOCORD). */
  reorder: (projectId: string, input: ReorderDocumentsInput) =>
    apiFetch<void>(`/projects/${projectId}/documents/reorder`, { method: 'POST', body: input }),

  remove: (documentId: string) => apiFetch<void>(`/documents/${documentId}`, { method: 'DELETE' }),

  templates: () => apiFetch<DocumentTemplateView[]>('/document-templates'),
};

export const documentKeys = {
  list: (projectId: string, query: DocumentQuery) =>
    ['document', projectId, query.q ?? '', query.kind ?? '', query.status ?? ''] as const,
  detail: (documentId: string) => ['document', 'detail', documentId] as const,
  templates: () => ['document', 'templates'] as const,
};
